import {
  Box,
  Flex,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { FEATURE_FLAGS } from '~/constants/featureFlags';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { addSteps } from '~/screens/MyAgency/components/FiltersDrawer/helpers';
import { useCreatorsFiltersStore } from '~/screens/MyAgency/store';
import { CriteriaProps } from '~/screens/MyAgency/types';
import { convertPxToRems } from '~/utils-ts';

import { talentFiltersTrackId } from './constants';

export const ages: CriteriaProps[] = [
  { label: '0', value: 0, display: true },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '60+', value: 60, display: true },
];

const convertValue = (value: number) =>
  value === ages[ages.length - 1].value ? `${value}+` : value;

export const SearchByAge: React.FC = () => {
  const { age, setAge } = useCreatorsFiltersStore();

  const marks = addSteps(ages);
  const [steps, setSteps] = useState([0, 60]);

  const onChange = useCallback(
    (value: number[]) => {
      setSteps(value);
    },
    [setSteps],
  );

  const onChangeEnd = useCallback(
    (value: number[]) => {
      setAge(`${value[0]}:${convertValue(value[1])}`);
    },
    [setAge],
  );

  useEffect(() => {
    if (age) {
      const firstAge = Number(age.split(':')[0]);
      const secondAge = age.split(':')[1].includes('+')
        ? Number(age.split(':')[1].replace('+', ''))
        : Number(age.split(':')[1]);

      setSteps([firstAge, secondAge]);
    } else {
      setSteps([0, 60]);
    }
  }, [age, setSteps]);

  const isGlobalFilterEnabled = useFeature(
    FEATURE_FLAGS.foamTalentGlobalFilters,
  );

  return (
    <Flex flexDirection="column" gap={2} track-id={talentFiltersTrackId.age}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize="lg" fontWeight="500" color="primary.500">
          Age
        </Text>
        <Flex>
          <RangeBox value={steps[0]} />
          <Text paddingX={1} color="primary.200">
            -
          </Text>
          <RangeBox value={steps[1]} />
        </Flex>
      </Flex>
      <Box position="relative">
        {/**
         * Since old and new design need to coexist,
         * we need to make design changes behind FF
         */}
        {!isGlobalFilterEnabled && (
          <Flex justifyContent="space-between">
            {marks
              .filter((mark) => mark.display)
              .map((mark) => (
                <Box key={mark.value}>
                  <Text fontSize="md">{mark.label}</Text>
                </Box>
              ))}
          </Flex>
        )}
        <Box position="relative" paddingX={1.5}>
          <RangeSlider
            defaultValue={steps}
            value={steps}
            min={0}
            max={60}
            onChange={onChange}
            onChangeEnd={onChangeEnd}
            track-id="foam-myagency-talentfilter-age"
          >
            <RangeSliderTrack background="secondary.50">
              <RangeSliderFilledTrack background="secondary.500" />
            </RangeSliderTrack>
            <RangeSliderThumb
              boxSize={3}
              index={0}
              background="secondary.500"
            />
            <RangeSliderThumb
              boxSize={3}
              index={1}
              background="secondary.500"
            />
          </RangeSlider>
        </Box>
      </Box>
    </Flex>
  );
};

const RangeBox: React.FC<{ value: number }> = ({ value }) => {
  const isGlobalFilterEnabled = useFeature(
    FEATURE_FLAGS.foamTalentGlobalFilters,
  ).isEnabled;

  return (
    <Flex
      flexDirection="column"
      borderRadius={convertPxToRems(8)}
      backgroundColor={isGlobalFilterEnabled ? 'primary.0' : 'primary.50'}
      color={value === 0 ? 'primary.200' : 'primary.400'}
      fontSize="md"
      fontWeight="medium"
      padding={2}
      height={6}
      justifyContent="center"
      alignItems="flex-end"
      data-testid="label"
    >
      {convertValue(value)}
    </Flex>
  );
};
